// Market & data finder.
// The rows are rendered server-side by the {{ market_finder:markets }} /
// {{ market_finder:commodities }} Antlers tags. This module reads those rows from
// the DOM, derives the filter options from them, and filters/sorts by toggling
// row visibility — no client-side row building, no embedded JSON.

class MarketFinder {
  constructor() {
    const root = document.querySelector(".market-finder");
    if (!root) return;
    if (!document.querySelector("#market-finder-tbody")) return;
    this.boot();
  }

  boot() {
    const $ = (s) => document.querySelector(s);
    const norm = (s) => s.toLowerCase().trim();

    const readProviders = (tr) =>
      [...tr.querySelectorAll(".market-finder__pill:not(.market-finder__pill--empty)")].map((p) => p.textContent.trim());

    // ---- Read server-rendered rows from the DOM ----
    const marketRows = [...document.querySelectorAll("#market-finder-tbody tr")].map((tr) => ({
      tr,
      pills: [...tr.querySelectorAll(".market-finder__pill")],
      country: tr.dataset.country,
      region: tr.dataset.region,
      marketKey: tr.dataset.market,
      marketLabel: tr.querySelector(".market-finder__cell-market").textContent.trim(),
      marketIndex: Number(tr.dataset.marketIndex),
      providers: readProviders(tr),
    }));

    const commodityRows = [...document.querySelectorAll("#market-finder-package-body tr")].map((tr) => ({
      tr,
      pills: [...tr.querySelectorAll(".market-finder__pill")],
      commodity: tr.dataset.commodity,
      product: tr.dataset.product,
      providers: readProviders(tr),
    }));

    // ---- Derive filter options from the rows (preserving server order) ----
    const regionsMap = new Map();
    marketRows.forEach((r) => {
      if (!regionsMap.has(r.region)) regionsMap.set(r.region, []);
      const list = regionsMap.get(r.region);
      if (!list.includes(r.country)) list.push(r.country);
    });
    const REGIONS = [...regionsMap.entries()].map(([name, countries]) => ({ name, countries }));

    const marketsMap = new Map();
    marketRows.forEach((r) => {
      if (!marketsMap.has(r.marketKey)) marketsMap.set(r.marketKey, { key: r.marketKey, label: r.marketLabel, index: r.marketIndex });
    });
    const MARKETS = [...marketsMap.values()].sort((a, b) => a.index - b.index);

    // Index/"product name" markets (e.g. gas indices) stay filterable but their
    // entries are not real providers, so keep them out of the provider dropdown.
    const indexKeys = new Set(MARKETS.filter((m) => /indices/i.test(m.label)).map((m) => m.key));

    const marketProviderSet = new Set();
    marketRows.forEach((r) => {
      if (!indexKeys.has(r.marketKey)) r.providers.forEach((p) => marketProviderSet.add(p));
    });
    const marketProviders = [...marketProviderSet].sort((a, b) => a.localeCompare(b));

    const commoditiesList = [];
    const commodityProviderSet = new Set();
    commodityRows.forEach((r) => {
      if (!commoditiesList.includes(r.commodity)) commoditiesList.push(r.commodity);
      r.providers.forEach((p) => commodityProviderSet.add(p));
    });
    const commodityProviders = [...commodityProviderSet].sort((a, b) => a.localeCompare(b));

    const state = {
      tab: "markets",
      markets: { search: "", country: new Set(), market: new Set(), provider: new Set(), sort: { key: "country", dir: "asc" } },
      commodities: { search: "", commodity: new Set(), provider: new Set() },
    };

    // ---- Custom multi-select component ----
    function closeAll() {
      document.querySelectorAll(".market-finder__select-panel").forEach((p) => (p.hidden = true));
      document.querySelectorAll(".market-finder__select-toggle").forEach((t) => t.setAttribute("aria-expanded", "false"));
      document.querySelectorAll(".market-finder__th-info.is-open").forEach((el) => el.classList.remove("is-open"));
    }
    document.addEventListener("click", closeAll);

    class MultiSelect {
      constructor(rootEl, opts) {
        this.root = rootEl;
        this.allLabel = opts.allLabel;
        this.noun = opts.noun;
        this.onChange = opts.onChange;
        this.selected = opts.selected;
        this.groups = opts.groups || null;
        this.options = opts.options || null;
        this.labelFor = opts.labelFor || ((v) => v);
        this.optEls = [];
        this.groupChecks = [];
        this.disabledValues = new Set();
        this.build();
      }
      build() {
        this.toggle = document.createElement("button");
        this.toggle.type = "button";
        this.toggle.className = "market-finder__select-toggle";
        this.toggle.setAttribute("aria-expanded", "false");
        this.panel = document.createElement("div");
        this.panel.className = "market-finder__select-panel";
        this.panel.hidden = true;

        const renderOpt = (value, cls) => {
          const lab = document.createElement("label");
          lab.className = "market-finder__select-option" + (cls ? " " + cls : "");
          const cb = document.createElement("input");
          cb.type = "checkbox";
          cb.value = value;
          cb.checked = this.selected.has(value);
          cb.addEventListener("change", () => {
            if (cb.checked) this.selected.add(value);
            else this.selected.delete(value);
            this.syncGroupStates();
            this.refresh();
            this.onChange();
          });
          lab.appendChild(cb);
          lab.appendChild(document.createTextNode(this.labelFor(value)));
          this.optEls.push({ value, lab, cb });
          return lab;
        };

        if (this.groups) {
          this.groups.forEach((g) => {
            const head = document.createElement("label");
            head.className = "market-finder__select-option market-finder__select-option--group";
            const gcb = document.createElement("input");
            gcb.type = "checkbox";
            gcb.addEventListener("change", () => {
              const enabled = g.options.filter((v) => !this.disabledValues.has(v) || this.selected.has(v));
              const all = enabled.every((v) => this.selected.has(v));
              enabled.forEach((v) => (all ? this.selected.delete(v) : this.selected.add(v)));
              this.rebuildChecks();
              this.refresh();
              this.onChange();
            });
            head.appendChild(gcb);
            head.appendChild(document.createTextNode(g.label));
            this.panel.appendChild(head);
            this.groupChecks.push({ cb: gcb, head, options: g.options });
            g.options.forEach((v) => this.panel.appendChild(renderOpt(v, "market-finder__select-option--child")));
          });
        } else {
          this.options.forEach((v) => this.panel.appendChild(renderOpt(v)));
        }

        this.toggle.addEventListener("click", (e) => {
          e.stopPropagation();
          const willOpen = this.panel.hidden;
          closeAll();
          if (willOpen) {
            this.panel.hidden = false;
            this.toggle.setAttribute("aria-expanded", "true");
          }
        });

        this.root.appendChild(this.toggle);
        this.root.appendChild(this.panel);
        this.syncGroupStates();
        this.refresh();
      }
      rebuildChecks() {
        this.optEls.forEach((o) => (o.cb.checked = this.selected.has(o.value)));
        this.syncGroupStates();
      }
      syncGroupStates() {
        this.groupChecks.forEach((g) => {
          const sel = g.options.filter((v) => this.selected.has(v)).length;
          g.cb.checked = sel === g.options.length;
          g.cb.indeterminate = sel > 0 && sel < g.options.length;
        });
      }
      setAvailable(availSet) {
        this.disabledValues = new Set();
        this.optEls.forEach((o) => {
          const ok = this.selected.has(o.value) || !availSet || availSet.has(o.value);
          o.lab.classList.toggle("market-finder__select-option--disabled", !ok);
          o.cb.disabled = !ok;
          if (!ok) this.disabledValues.add(o.value);
        });
        this.groupChecks.forEach((g) => {
          const allOff = g.options.every((v) => this.disabledValues.has(v));
          g.head.classList.toggle("market-finder__select-option--disabled", allOff);
          g.cb.disabled = allOff;
        });
      }
      refresh() {
        const n = this.selected.size;
        let text = this.allLabel;
        if (n === 1) text = this.labelFor([...this.selected][0]);
        else if (n > 1) text = `${n} ${this.noun} selected`;
        this.toggle.textContent = text;
        this.toggle.setAttribute("data-active", n > 0 ? "true" : "false");
      }
      reset() {
        this.rebuildChecks();
        this.refresh();
      }
    }

    let msCountry, msMarket, msProviderM, msCommodity, msProviderC;

    // ---- Filter predicates (ignore one facet for availability calc) ----
    const passMarket = (r, ignore) => {
      const M = state.markets;
      if (ignore !== "country" && M.country.size && !M.country.has(r.country)) return false;
      if (ignore !== "market" && M.market.size && !M.market.has(r.marketKey)) return false;
      if (ignore !== "provider" && M.provider.size && !r.providers.some((p) => M.provider.has(p))) return false;
      if (M.search) {
        const q = norm(M.search);
        if (!norm(r.country).includes(q) && !norm(r.marketLabel).includes(q) && !r.providers.some((p) => norm(p).includes(q))) return false;
      }
      return true;
    };

    const passCommodity = (r, ignore) => {
      const C = state.commodities;
      if (ignore !== "commodity" && C.commodity.size && !C.commodity.has(r.commodity)) return false;
      if (ignore !== "provider" && C.provider.size && !r.providers.some((p) => C.provider.has(p))) return false;
      if (C.search) {
        const q = norm(C.search);
        if (!norm(r.commodity).includes(q) && !norm(r.product).includes(q) && !r.providers.some((p) => norm(p).includes(q))) return false;
      }
      return true;
    };

    const providersInRows = (rows) => {
      const s = new Set();
      rows.forEach((r) => r.providers.forEach((p) => s.add(p)));
      return s;
    };

    const togglePillMatch = (r, providerSet) => {
      r.pills.forEach((pill) => {
        if (pill.classList.contains("market-finder__pill--empty")) return;
        const match = providerSet.size && providerSet.has(pill.textContent.trim());
        pill.classList.toggle("market-finder__pill--match", match);
      });
    };

    const sortMarketRows = () => {
      const dir = state.markets.sort.dir === "asc" ? 1 : -1;
      const cmp = {
        country: (a, b) => a.country.localeCompare(b.country) || a.marketIndex - b.marketIndex,
        market: (a, b) => a.marketIndex - b.marketIndex || a.country.localeCompare(b.country),
      }[state.markets.sort.key];
      return marketRows.slice().sort((a, b) => cmp(a, b) * dir);
    };

    const renderMarkets = () => {
      // Reorder the DOM rows to the current sort
      const tbody = $("#market-finder-tbody");
      const frag = document.createDocumentFragment();
      sortMarketRows().forEach((r) => frag.appendChild(r.tr));
      tbody.appendChild(frag);

      let visible = 0;
      marketRows.forEach((r) => {
        const show = passMarket(r, null);
        r.tr.hidden = !show;
        if (show) visible++;
        togglePillMatch(r, state.markets.provider);
      });

      $("#market-finder-table-wrap").hidden = visible === 0;
      $("#market-finder-empty-markets").hidden = visible > 0;
      $("#tab-count-markets").textContent = visible;

      msCountry.setAvailable(new Set(marketRows.filter((r) => passMarket(r, "country")).map((r) => r.country)));
      msMarket.setAvailable(new Set(marketRows.filter((r) => passMarket(r, "market")).map((r) => r.marketKey)));
      msProviderM.setAvailable(providersInRows(marketRows.filter((r) => passMarket(r, "provider"))));
    };

    const renderCommodities = () => {
      let visible = 0;
      commodityRows.forEach((r) => {
        const show = passCommodity(r, null);
        r.tr.hidden = !show;
        if (show) visible++;
        togglePillMatch(r, state.commodities.provider);
      });

      $("#market-finder-package-wrap").hidden = visible === 0;
      $("#market-finder-empty-commodities").hidden = visible > 0;
      $("#tab-count-commodities").textContent = visible;

      msCommodity.setAvailable(new Set(commodityRows.filter((r) => passCommodity(r, "commodity")).map((r) => r.commodity)));
      msProviderC.setAvailable(providersInRows(commodityRows.filter((r) => passCommodity(r, "provider"))));
    };

    const updateSortIndicators = () => {
      document.querySelectorAll(".market-finder__table thead th[data-sort]").forEach((th) => {
        const arrow = th.querySelector(".arrow");
        if (!arrow) return;
        if (th.dataset.sort === state.markets.sort.key) {
          th.setAttribute("aria-sort", state.markets.sort.dir === "asc" ? "ascending" : "descending");
          arrow.textContent = state.markets.sort.dir === "asc" ? "↓" : "↑";
        } else {
          th.removeAttribute("aria-sort");
          arrow.textContent = "↕";
        }
      });
    };

    const updateReset = (isMarkets) => {
      const active = isMarkets
        ? state.markets.search || state.markets.country.size || state.markets.market.size || state.markets.provider.size
        : state.commodities.search || state.commodities.commodity.size || state.commodities.provider.size;
      $("#market-finder-reset").classList.toggle("market-finder__reset--active", active);
    };

    const render = () => {
      const isMarkets = state.tab === "markets";
      $("#panel-markets").hidden = !isMarkets;
      $("#panel-commodities").hidden = isMarkets;
      $("#filters-markets").hidden = !isMarkets;
      $("#filters-commodities").hidden = isMarkets;
      $("#tab-markets").setAttribute("aria-selected", isMarkets);
      $("#tab-commodities").setAttribute("aria-selected", !isMarkets);

      renderMarkets();
      renderCommodities();
      updateSortIndicators();
      updateReset(isMarkets);
    };

    // ---- Wire up ----
    msCountry = new MultiSelect($("#market-finder-country"), {
      allLabel: "All countries", noun: "countries", selected: state.markets.country,
      groups: REGIONS.map((r) => ({ label: r.name, options: r.countries })),
      onChange: render,
    });
    msMarket = new MultiSelect($("#market-finder-market"), {
      allLabel: "All market types", noun: "markets", selected: state.markets.market,
      options: MARKETS.map((m) => m.key),
      labelFor: (k) => (MARKETS.find((m) => m.key === k) || {}).label || k,
      onChange: render,
    });
    msProviderM = new MultiSelect($("#market-finder-provider-m"), {
      allLabel: "All providers", noun: "providers", selected: state.markets.provider,
      options: marketProviders,
      onChange: render,
    });
    msCommodity = new MultiSelect($("#market-finder-commodity"), {
      allLabel: "All commodities", noun: "commodities", selected: state.commodities.commodity,
      options: commoditiesList,
      onChange: render,
    });
    msProviderC = new MultiSelect($("#market-finder-provider-c"), {
      allLabel: "All providers", noun: "providers", selected: state.commodities.provider,
      options: commodityProviders,
      onChange: render,
    });

    document.querySelectorAll(".market-finder__table thead th[data-sort]").forEach((th) => {
      th.addEventListener("click", () => {
        const key = th.dataset.sort;
        const s = state.markets.sort;
        if (s.key === key) s.dir = s.dir === "asc" ? "desc" : "asc";
        else {
          s.key = key;
          s.dir = "asc";
        }
        render();
      });
    });

    $("#tab-markets").addEventListener("click", () => {
      state.tab = "markets";
      render();
    });
    $("#tab-commodities").addEventListener("click", () => {
      state.tab = "commodities";
      render();
    });

    const filterToggle = $("#market-finder-filter-toggle");
    if (filterToggle) {
      filterToggle.addEventListener("click", () => {
        const open = $(".market-finder__controls").classList.toggle("is-open");
        filterToggle.setAttribute("aria-expanded", open);
      });
    }

    $("#search-markets").addEventListener("input", (e) => {
      state.markets.search = e.target.value;
      render();
    });
    $("#search-commodities").addEventListener("input", (e) => {
      state.commodities.search = e.target.value;
      render();
    });

    $("#market-finder-reset").addEventListener("click", () => {
      if (state.tab === "markets") {
        const M = state.markets;
        M.search = "";
        $("#search-markets").value = "";
        M.country.clear();
        M.market.clear();
        M.provider.clear();
        msCountry.reset();
        msMarket.reset();
        msProviderM.reset();
      } else {
        const C = state.commodities;
        C.search = "";
        $("#search-commodities").value = "";
        C.commodity.clear();
        C.provider.clear();
        msCommodity.reset();
        msProviderC.reset();
      }
      render();
    });

    // Real-time tooltip: toggle on click, close on outside click / X
    document.querySelectorAll(".market-finder__info").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const wrap = btn.closest(".market-finder__th-info");
        const willOpen = !wrap.classList.contains("is-open");
        closeAll();
        if (willOpen) wrap.classList.add("is-open");
      });
    });
    document.querySelectorAll(".market-finder__tooltip").forEach((tip) => tip.addEventListener("click", (e) => e.stopPropagation()));
    document.querySelectorAll(".market-finder__tooltip-close").forEach((btn) => btn.addEventListener("click", closeAll));

    render();
  }
}

export default MarketFinder;
